import { useCallback, useEffect, useRef, useState } from "react";
import type { Locale } from "../shared/dto";
import type { ClientOpenTarget } from "./rail-model";
import { CLIENT_NOTIFICATION_SURFACES } from "../shared/notification-targets";
import { intlLanguage } from "./date-format";
import { ToastLayer } from "./toast-layer";

export interface InboxItem {
  id: string;
  type: string;
  kind: "call" | "step" | "delivery" | "system";
  /** `action` is the partner's move (blue), distinct from `success` =
   * finished (green). Merging the two made "ke kontrole" and "potvrzeno"
   * the same dot (audit P-14). */
  severity: "neutral" | "action" | "success" | "warning" | "error";
  title: string;
  body: string;
  url: string;
  createdAt: string;
  read: boolean;
  action?: {
    type: "confirm_step";
    step: "apple" | "profile";
    primaryLabel: string;
    label: string;
  };
}

export interface GroupedInboxItem extends InboxItem {
  ids: string[];
  count: number;
}

interface InboxResponse {
  ok: true;
  items: InboxItem[];
  unreadCount: number;
}

export interface InboxSnapshot {
  clientId: string;
  itemIds: string[];
}

export type InboxSurface = ClientOpenTarget;

// F131: one list, in `shared/notification-targets.ts`. This used to be the
// third copy, and a surface missing from any one of them was a link the bell
// silently downgraded to the overview.
const INBOX_SURFACES: ReadonlySet<string> = new Set(CLIENT_NOTIFICATION_SURFACES);
const KNOWHOW_NOTIFICATION_TYPES = new Set(["knowhow_folder_shared", "knowhow_resources_ready"]);

/** How long the pop-up card stays before it hands the message back to the
 * bell (audit S2-18). Long enough to read two lines and reach a button,
 * short enough that a corner of the screen is never permanently lost. */
export const TOAST_VISIBLE_MS = 12_000;

export function inboxDestination(
  item: Pick<InboxItem, "type" | "url">
): { mode: "surface"; surface: InboxSurface } | { mode: "reload"; url: string } {
  // Old stored notifications can carry the pre-D21 call destination. The
  // event type is the durable truth for the two know-how completion events.
  if (KNOWHOW_NOTIFICATION_TYPES.has(item.type)) {
    return { mode: "surface", surface: "knowhow" };
  }
  try {
    const base = typeof window === "undefined" ? "https://launchpad.local" : window.location.origin;
    const parsed = new URL(item.url, base);
    if (parsed.origin !== new URL(base).origin) {
      return { mode: "reload", url: item.url };
    }
    // Staff notifications carry a real client and workspace destination.
    // Treating every root-path URL without `open` as client home collapsed
    // their href to "/" and broke new-tab, copy-link and no-JS navigation.
    if (parsed.searchParams.has("client") || parsed.searchParams.has("staffStep")) {
      return { mode: "reload", url: `${parsed.pathname}${parsed.search}${parsed.hash}` };
    }
    const opened = parsed.searchParams.get("open");
    if (opened && INBOX_SURFACES.has(opened as InboxSurface)) {
      return { mode: "surface", surface: opened as InboxSurface };
    }
    if (parsed.pathname === "/" && !opened) {
      return { mode: "surface", surface: "home" };
    }
  } catch {
    // Invalid or external URLs keep the existing full-navigation fallback.
  }
  return { mode: "reload", url: item.url };
}

export function inboxItemMatchesSurface(item: Pick<InboxItem, "type" | "url">, surface: InboxSurface): boolean {
  if (KNOWHOW_NOTIFICATION_TYPES.has(item.type)) return surface === "knowhow";
  try {
    const opened = new URL(item.url, "https://launchpad.local").searchParams.get("open");
    return opened !== null && opened === surface && INBOX_SURFACES.has(surface);
  } catch {
    return false;
  }
}

export function acceptInboxReadResponse(
  responseClientId: string,
  activeClientId: string,
  responseSequence: number,
  activeSequence: number
): boolean {
  return responseClientId === activeClientId && responseSequence === activeSequence;
}

function inboxHref(item: Pick<InboxItem, "type" | "url">): string {
  const destination = inboxDestination(item);
  if (destination.mode === "reload") return destination.url;
  return destination.surface === "home"
    ? "/"
    : `/?open=${encodeURIComponent(destination.surface)}`;
}

export function NotificationCenter(props: {
  activeClientId: string;
  csrfToken: string;
  locale: Locale;
  t: (key: string) => string;
  onConfirmed?: () => void | Promise<void>;
  onNavigate?: (surface: InboxSurface) => void;
  onOpened?: () => void | Promise<void>;
  // F39: a new notification means something changed behind the client's back.
  // The rail and the surfaces must show it WITHOUT a page reload, so the
  // inbox hands the freshly detected change to the dashboard.
  onLiveUpdate?: () => void | Promise<void>;
  activeSurface?: InboxSurface;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [readingId, setReadingId] = useState<string | "all" | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmationErrorId, setConfirmationErrorId] = useState<string | null>(null);
  const [toast, setToast] = useState<InboxItem | null>(null);
  // S2-18: the card had no timer anywhere, so it sat in the corner until
  // somebody closed it - measured at over ten minutes, covering the right end
  // of two diagnostics rows and swallowing the clicks underneath (the layer is
  // pointer-transparent, the card is not). The bell keeps every message, so
  // the POP-UP can go on its own; hovering or focusing it holds it open, which
  // is what makes an auto-dismissing card with buttons safe to use.
  const [toastHeld, setToastHeld] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const snapshotRef = useRef<InboxSnapshot | null>(null);
  const activeClientRef = useRef(props.activeClientId);
  const confirmCommandsRef = useRef(new Map<string, string>());
  const loadSequenceRef = useRef(0);
  const readSequenceRef = useRef(0);
  activeClientRef.current = props.activeClientId;

  const loadInbox = useCallback(async (quiet = false) => {
    const responseClientId = props.activeClientId;
    const loadSequence = ++loadSequenceRef.current;
    if (!quiet) setState("loading");
    try {
      const response = await fetch("/api/notifications/inbox", { credentials: "include" });
      if (!response.ok) throw new Error("inbox_failed");
      const body = await response.json() as InboxResponse;
      if (loadSequence !== loadSequenceRef.current) return;
      const next = reconcileInboxSnapshot(
        snapshotRef.current,
        responseClientId,
        activeClientRef.current,
        body.items
      );
      if (!next.accepted || !next.snapshot) return;
      snapshotRef.current = next.snapshot;
      if (next.toast) {
        setToast(next.toast);
        void props.onLiveUpdate?.();
      }
      setItems(body.items);
      setUnreadCount(body.unreadCount);
      setState("ready");
    } catch {
      if (!quiet && loadSequence === loadSequenceRef.current && responseClientId === activeClientRef.current) {
        setState("error");
      }
    }
  }, [props.activeClientId]);

  useEffect(() => {
    readSequenceRef.current += 1;
    snapshotRef.current = null;
    setItems([]);
    setUnreadCount(0);
    setToast(null);
    setConfirmationErrorId(null);
    setReadingId(null);
    void loadInbox();
    const interval = window.setInterval(() => void loadInbox(true), 60_000);
    const onVisible = () => {
      if (!document.hidden) void loadInbox(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadInbox]);

  useEffect(() => {
    if (!toast || toastHeld) return;
    const timer = window.setTimeout(() => setToast(null), TOAST_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [toast, toastHeld]);

  useEffect(() => {
    const responseSequence = ++readSequenceRef.current;
    const surface = props.activeSurface;
    if (!surface || !items.some((item) => !item.read && inboxItemMatchesSurface(item, surface))) return;
    const responseClientId = props.activeClientId;
    void fetch("/api/notifications/inbox/read", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken },
      body: JSON.stringify({ surface })
    }).then(async (response) => {
      if (!response.ok || !acceptInboxReadResponse(
        responseClientId,
        activeClientRef.current,
        responseSequence,
        readSequenceRef.current
      )) return;
      const body = await response.json() as { unreadCount: number };
      if (!acceptInboxReadResponse(
        responseClientId,
        activeClientRef.current,
        responseSequence,
        readSequenceRef.current
      )) return;
      setUnreadCount(body.unreadCount);
      setItems((current) => current.map((item) => inboxItemMatchesSurface(item, surface) ? { ...item, read: true } : item));
    }).catch(() => undefined);
  }, [items, props.activeClientId, props.activeSurface, props.csrfToken]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  async function markRead(input: { notificationId: string } | { all: true }): Promise<boolean> {
    const responseClientId = props.activeClientId;
    const responseSequence = ++readSequenceRef.current;
    setReadingId("all" in input ? "all" : input.notificationId);
    try {
      const response = await fetch("/api/notifications/inbox/read", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken
        },
        body: JSON.stringify(input)
      });
      if (!response.ok) throw new Error("read_failed");
      const body = await response.json() as { unreadCount: number };
      if (acceptInboxReadResponse(
        responseClientId,
        activeClientRef.current,
        responseSequence,
        readSequenceRef.current
      )) {
        setUnreadCount(body.unreadCount);
        if ("all" in input) setItems((current) => current.map((item) => ({ ...item, read: true })));
        else setItems((current) => current.map((item) => item.id === input.notificationId ? { ...item, read: true } : item));
      }
      return true;
    } catch {
      if (acceptInboxReadResponse(
        responseClientId,
        activeClientRef.current,
        responseSequence,
        readSequenceRef.current
      )) setState("error");
      return false;
    } finally {
      setReadingId(null);
    }
  }

  async function confirmAction(item: InboxItem): Promise<void> {
    if (!item.action || confirmingId || snapshotRef.current?.clientId !== props.activeClientId) return;
    setConfirmingId(item.id);
    setConfirmationErrorId(null);
    let commandId = confirmCommandsRef.current.get(item.id);
    if (!commandId) {
      commandId = crypto.randomUUID();
      confirmCommandsRef.current.set(item.id, commandId);
    }
    let response: Response;
    try {
      response = await fetch(`/api/client-steps/${item.action.step}/confirm`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": commandId
        },
        body: "{}"
      });
    } catch {
      setConfirmationErrorId(item.id);
      setConfirmingId(null);
      return;
    }
    confirmCommandsRef.current.delete(item.id);
    if (!response.ok) {
      setConfirmationErrorId(item.id);
      setConfirmingId(null);
      return;
    }
    if (toast?.id === item.id) setToast(null);
    try {
      await loadInbox(true);
      await props.onConfirmed?.();
    } finally {
      setConfirmingId(null);
    }
  }

  const displayItems = groupInboxItems(items);

  function closePanel() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  async function openItem(item: GroupedInboxItem | InboxItem): Promise<void> {
    const ids = "ids" in item ? item.ids : [item.id];
    for (const notificationId of ids) {
      const original = items.find((candidate) => candidate.id === notificationId);
      if (original && !original.read && !await markRead({ notificationId })) return;
    }
    const destination = inboxDestination(item);
    if (destination.mode === "surface" && props.onNavigate) {
      setOpen(false);
      setToast(null);
      props.onNavigate(destination.surface);
      await props.onOpened?.();
      return;
    }
    window.location.assign(destination.mode === "reload" ? destination.url : item.url);
  }

  return (
    <div className={`notification-center${open ? " open" : ""}`} ref={rootRef}>
      <button
        className="notification-trigger"
        type="button"
        ref={triggerRef}
        aria-label={props.t("notifications.inbox.open")}
        aria-expanded={open}
        aria-controls="notification-inbox-panel"
        onClick={() => {
          setOpen((current) => !current);
          if (!open) void loadInbox(true);
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 8.5h18C21 16 18 16 18 9ZM9.5 20a3 3 0 0 0 5 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge" aria-label={props.t("notifications.inbox.unreadCount").replace("{count}", String(unreadCount))}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="notification-backdrop" aria-hidden="true" onClick={closePanel} />
          <section
            id="notification-inbox-panel"
            className="notification-panel"
            role="dialog"
            aria-labelledby="notification-panel-title"
          >
          <header className="notification-panel-heading">
            <div>
              <p className="eyebrow blue-eyebrow">{props.t("notifications.inbox.eyebrow")}</p>
              <h2 id="notification-panel-title">{props.t("notifications.inbox.title")}</h2>
            </div>
            <div className="notification-panel-actions">
              {unreadCount > 0 && (
                <button className="notification-read-all" type="button" disabled={readingId !== null} onClick={() => void markRead({ all: true })}>
                  {props.t("notifications.inbox.readAll")}
                </button>
              )}
              <button
                className="notification-close"
                type="button"
                ref={closeRef}
                aria-label={props.t("notifications.inbox.close")}
                onClick={closePanel}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
          </header>

          {state === "loading" && <p className="notification-panel-state" role="status">{props.t("notifications.inbox.loading")}</p>}
          {state === "error" && (
            <div className="notification-panel-state" role="alert">
              <p>{props.t("notifications.inbox.error")}</p>
              <button className="quiet-link" type="button" onClick={() => void loadInbox()}>{props.t("common.retry")}</button>
            </div>
          )}
          {state === "ready" && displayItems.length === 0 && (
            <p className="notification-panel-state">{props.t("notifications.inbox.empty")}</p>
          )}
          {state === "ready" && displayItems.length > 0 && (
            <>
              <ol className="notification-list">
              {displayItems.map((item) => (
                <li className={item.read ? "read" : "unread"} key={item.id}>
                  <div className="notification-item">
                    <a
                      href={inboxHref(item)}
                      onClick={(event) => {
                        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                        event.preventDefault();
                        void openItem(item);
                      }}
                      aria-busy={readingId !== null && readingId !== "all" && item.ids.includes(readingId)}
                    >
                      <span className="notification-marker">
                        <span className={`notification-kind kind-${item.kind} severity-${item.severity}`} aria-hidden="true" />
                        {item.severity === "error" && (
                          <NotificationSeverityIcon label={props.t("notifications.inbox.errorSeverity")} />
                        )}
                      </span>
                      <span className="notification-copy">
                        <strong>{item.title}</strong>
                        <span>{item.body}</span>
                        {item.count > 1 && (
                          <span className="notification-group-count">
                            {props.t("notifications.inbox.groupCount").replace("{count}", String(item.count))}
                          </span>
                        )}
                        <time dateTime={item.createdAt}>{formatTimestamp(item.createdAt, props.locale)}</time>
                      </span>
                      {!item.read && <span className="notification-unread-dot" aria-label={props.t("notifications.inbox.unread")} />}
                    </a>
                    {item.action && (
                      <div className="notification-inline-controls">
                        <button
                          className="notification-inline-primary"
                          type="button"
                          onClick={() => void openItem(item)}
                        >
                          <ActionIcon step={item.action.step} action="open" />
                          {item.action.primaryLabel}
                        </button>
                        <button
                          className="notification-inline-action"
                          type="button"
                          disabled={confirmingId !== null}
                          onClick={() => void confirmAction(item)}
                        >
                          <ActionIcon step={item.action.step} action="confirm" />
                          {confirmingId === item.id ? props.t("notifications.inbox.confirmSending") : item.action.label}
                        </button>
                        {confirmationErrorId === item.id && (
                          <span role="alert">{props.t("notifications.inbox.confirmError")}</span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
              </ol>
              {unreadCount > 0 && (
                <button
                  className="notification-read-all notification-read-all-mobile"
                  type="button"
                  disabled={readingId !== null}
                  onClick={() => void markRead({ all: true })}
                >
                  {props.t("notifications.inbox.readAll")}
                </button>
              )}
            </>
          )}
          </section>
        </>
      )}

      {/* The bell lives in the sticky operator header, and that header's
          backdrop-filter would make a fixed toast header-relative instead of
          viewport-relative. The layer hangs on <body>, so the toast always
          lands in the bottom right corner, whole. */}
      {toast && (
        <ToastLayer>
        <aside
          className="notification-toast"
          onMouseEnter={() => setToastHeld(true)}
          onMouseLeave={() => setToastHeld(false)}
          onFocusCapture={() => setToastHeld(true)}
          onBlurCapture={() => setToastHeld(false)}
          role="status"
          aria-live="polite"
          aria-label={props.t("notifications.inbox.toastLabel")}
        >
          <span className="notification-marker">
            <span className={`notification-kind kind-${toast.kind} severity-${toast.severity}`} aria-hidden="true" />
            {toast.severity === "error" && (
              <NotificationSeverityIcon label={props.t("notifications.inbox.errorSeverity")} />
            )}
          </span>
          <span className="notification-toast-copy">
            <strong>{toast.title}</strong>
            <span>{toast.body}</span>
            <time dateTime={toast.createdAt}>{formatTimestamp(toast.createdAt, props.locale)}</time>
          </span>
          <button
            className="notification-toast-close"
            type="button"
            aria-label={props.t("notifications.inbox.toastClose")}
            onClick={() => setToast(null)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
          <div className="notification-toast-actions">
            {toast.action ? (
              <>
                <button type="button" className="notification-toast-open" onClick={() => void openItem(toast)}>
                  <ActionIcon step={toast.action.step} action="open" />
                  {toast.action.primaryLabel}
                </button>
                <button
                  type="button"
                  className="notification-toast-confirm"
                  disabled={confirmingId !== null}
                  onClick={() => void confirmAction(toast)}
                >
                  <ActionIcon step={toast.action.step} action="confirm" />
                  {confirmingId === toast.id ? props.t("notifications.inbox.confirmSending") : toast.action.label}
                </button>
              </>
            ) : (
              <button type="button" className="notification-toast-open" onClick={() => void openItem(toast)}>
                {props.t("notifications.inbox.toastOpen")}
              </button>
            )}
          </div>
          {confirmationErrorId === toast.id && (
            <span className="notification-toast-error" role="alert">{props.t("notifications.inbox.confirmError")}</span>
          )}
        </aside>
        </ToastLayer>
      )}
    </div>
  );
}

function ActionIcon({ step, action }: { step: "apple" | "profile"; action: "open" | "confirm" }) {
  if (action === "confirm") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12.5 4.25 4.25L19 7" /></svg>;
  }
  return step === "profile"
    ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5c5.5 0 9 7 9 7s-3.5 7-9 7-9-7-9-7 3.5-7 9-7Zm0 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" /></svg>
    : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 7h8M7 11h10M9 15h6M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" /></svg>;
}

function NotificationSeverityIcon({ label }: { label: string }) {
  return (
    <span className="notification-severity-icon" role="img" aria-label={label}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 8v5M12 17h.01M10.3 3.8 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" />
      </svg>
    </span>
  );
}

export function reconcileInboxSnapshot(
  previous: InboxSnapshot | null,
  responseClientId: string,
  activeClientId: string,
  items: InboxItem[]
): { accepted: boolean; snapshot: InboxSnapshot | null; toast: InboxItem | null } {
  if (responseClientId !== activeClientId) {
    return { accepted: false, snapshot: previous, toast: null };
  }
  const firstForClient = previous?.clientId !== responseClientId;
  const knownIds = new Set(firstForClient ? [] : previous.itemIds);
  const toast = firstForClient
    ? null
    : items.find((item) => !item.read && !knownIds.has(item.id)) ?? null;
  return {
    accepted: true,
    snapshot: { clientId: responseClientId, itemIds: items.map((item) => item.id) },
    toast
  };
}

export function groupInboxItems(items: InboxItem[]): GroupedInboxItem[] {
  const groups = new Map<string, GroupedInboxItem>();
  for (const item of items) {
    const normalized = item.title
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase("cs")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
    const legacySalesPlan = normalized.includes("novy podklad ke kontrole")
      && normalized.includes("prodejni plan");
    // Repeated delivery receipts for the same lifecycle event used to bury
    // current work. The destination and normalized copy form the stable
    // display identity; distinct destinations or messages stay separate.
    const key = legacySalesPlan
      ? "legacy-sales-plan"
      : `${item.type}:${item.url}:${normalized}:${item.body.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("cs")}`;
    const current = groups.get(key);
    if (!current) {
      groups.set(key, { ...item, ids: [item.id], count: 1 });
      continue;
    }
    current.ids.push(item.id);
    current.count += 1;
    current.read = current.read && item.read;
  }
  return [...groups.values()];
}

export function formatTimestamp(value: string, locale: Locale, now: Date = new Date()): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  // F116: en-GB printed the bell as "4 Aug 2026 14:32" - day before month on a
  // 24-hour clock, which an American reads twice. The clock follows the tag
  // too: `hourCycle` is stated only for Czech, so English gets its own AM/PM.
  const language = intlLanguage(locale);
  const dateKey = (candidate: Date) =>
    new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "Europe/Prague"
    })
      .formatToParts(candidate)
      .filter((part) => part.type !== "literal")
      .map((part) => part.value)
      .join("-");
  const time = new Intl.DateTimeFormat(language, {
    hour: "2-digit",
    minute: "2-digit",
    ...(locale === "cs" ? { hourCycle: "h23" as const } : {}),
    timeZone: "Europe/Prague"
  }).format(date);
  if (dateKey(date) === dateKey(now)) {
    return `${locale === "cs" ? "dnes" : "today"} ${time}`;
  }
  const day = new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: locale === "cs" ? "numeric" : "short",
    year: "numeric",
    timeZone: "Europe/Prague"
  }).format(date);
  return `${day} ${time}`;
}
